// Phase 26 correction #3 — render-scoping helper for live time readouts.
//
// A running task timer's elapsed display reads Date.now() at render, so it needs
// something to trigger a re-render periodically. Previously a single App-level
// `setTimerTick` interval re-rendered the ENTIRE App every 5s while any timer ran
// — repainting all rows and any open modal (the mid-edit flicker).
//
// SelfTick localises that: it re-renders ONLY itself on the interval and calls
// `children()` to recompute the display. Wrap just the time-derived readout in it
// and nothing else in the tree re-renders on the tick.
//
// Phase 27: default interval dropped from 5s to 1s so live elapsed readouts
// count in real time (H:MM:SS). Because SelfTick scopes the re-render to the
// wrapped readout only, ticking each second stays cheap — the pom ring sharing
// the same wrapper re-rendering each second is fine.
//
// Usage: <SelfTick active={isRunning}>{() => formatElapsed(timer)}</SelfTick>
import { useState, useEffect, type ReactNode } from "react";

type SelfTickProps = {
  active?: boolean;
  intervalMs?: number;
  children?: ReactNode | (() => ReactNode);
};

export default function SelfTick({
  active = true,
  intervalMs = 1000,
  children,
}: SelfTickProps) {
  const [, force] = useState(0);
  useEffect(() => {
    if (!active) return;
    const iv = setInterval(() => force((x) => (x + 1) % 1e9), intervalMs);
    return () => clearInterval(iv);
  }, [active, intervalMs]);
  return typeof children === "function" ? children() : children || null;
}
